<template>
  <div class="company-detail-view">
    <el-page-header 
      @back="goBack" 
      title="公司管理"
      :content="companyStore.currentCompany?.name || '公司详情'"
    />

    <div class="loading-container" v-if="companyStore.loading">
      <el-skeleton :rows="10" animated />
    </div>
    
    <div v-else-if="!companyStore.currentCompany" class="empty-container">
      <el-empty description="未找到公司信息">
        <el-button type="primary" @click="goBack">返回列表</el-button>
      </el-empty>
    </div>
    
    <template v-else>
      <div class="company-detail-container">
        <!-- 公司基本信息 -->
        <el-card class="detail-card">
          <template #header>
            <div class="card-header">
              <div class="card-title">
                <h2>{{ companyStore.currentCompany.name }}</h2>
                <div class="company-flags">
                  <el-tag v-if="companyStore.currentCompany.isStarred" type="warning" effect="dark">
                    <el-icon><Star /></el-icon> 已收藏
                  </el-tag>
                  <el-tag v-if="companyStore.currentCompany.isPinned" type="warning" effect="dark">
                    <el-icon><Top /></el-icon> 已置顶
                  </el-tag>
                </div>
              </div>
              <div class="card-actions">
                <el-button type="primary" @click="openEditDialog">
                  <el-icon><Edit /></el-icon> 编辑
                </el-button>
                <el-button type="danger" @click="confirmDelete">
                  <el-icon><Delete /></el-icon> 删除
                </el-button>
              </div>
            </div>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <span class="label">公司类型</span>
              <span class="value">{{ companyStore.currentCompany.companyType }}</span>
            </div>
            
            <div class="info-item">
              <span class="label">行业</span>
              <span class="value">{{ companyStore.currentCompany.industry || '未设置' }}</span>
            </div>
            
            <div class="info-item">
              <span class="label">所在地</span>
              <span class="value">{{ companyStore.currentCompany.location || '未设置' }}</span>
            </div>
            
            <div class="info-item status-item">
              <span class="label">状态</span>
              <CompanyStatusTag :status="companyStore.currentCompany.status" />
            </div>
            
            <div class="info-item">
              <span class="label">联系人</span>
              <span class="value">{{ companyStore.currentCompany.contactPerson || '未设置' }}</span>
            </div>
            
            <div class="info-item full-width">
              <span class="label">招聘官网</span>
              <el-link 
                :href="formatUrl(companyStore.currentCompany.recruitmentUrl)" 
                target="_blank" 
                type="primary"
              >
                {{ companyStore.currentCompany.recruitmentUrl }}
              </el-link>
            </div>
            
            <div class="info-item full-width" v-if="companyStore.currentCompany.notes">
              <span class="label">备注</span>
              <div class="note-content">
                {{ companyStore.currentCompany.notes }}
              </div>
            </div>
            
            <div class="info-item">
              <span class="label">创建时间</span>
              <span class="value">{{ companyStore.currentCompany.createTime || '未知' }}</span>
            </div>
            
            <div class="info-item">
              <span class="label">更新时间</span>
              <span class="value">{{ companyStore.currentCompany.updateTime || '未知' }}</span>
            </div>
          </div>
        </el-card>
      
        <!-- Add positions section -->
        <el-card class="position-card" v-if="companyStore.currentCompany.id" id="positions">
          <template #header>
            <div class="card-header">
              <h3>关联职位</h3>
              <el-button type="primary" size="small" @click="showAddPositionDialog">添加职位</el-button>
            </div>
          </template>
          
          <div v-if="positionStore.loading" class="loading-container">
            <el-skeleton :rows="3" animated />
          </div>
          
          <div v-else-if="positionStore.positions.length === 0" class="empty-state">
            <el-empty description="暂无关联职位" />
          </div>
          
          <div v-else class="positions-list">
            <div
              v-for="position in positionStore.positions"
              :key="position.id"
              class="position-list-item"
            >
              <div class="position-item-content" @click="handlePositionClick(position)">
                <div class="position-info">
                  <h3 class="position-title">{{ position.title }}</h3>
                  <div class="position-tags">
                    <application-status-tag :status="position.applicationStatus" size="small" />
                    <interview-status-tag :status="position.interviewStatus" size="small" />
                    <el-tag size="small" effect="plain">{{ getWorkTypeLabel(position.workType) }}</el-tag>
                  </div>
                </div>
                <div class="position-meta">
                  <span v-if="position.minSalary && position.maxSalary" class="salary-range">
                    {{ position.minSalary }}-{{ position.maxSalary }} {{ position.salaryCurrency || 'CNY' }}
                  </span>
                  <span v-if="position.deadline" class="deadline">
                    <el-icon><Calendar /></el-icon>
                    截止日期: {{ formatDate(position.deadline) }}
                  </span>
                </div>
              </div>
              <div class="position-actions">
                <el-tooltip content="查看资源" placement="top">
                  <div class="action-icon view" @click="handleViewResources(position)">
                    <el-icon><Collection /></el-icon>
                  </div>
                </el-tooltip>
                <el-tooltip content="查看面试记录" placement="top">
                  <div class="action-icon interview" @click="navigateToInterviews(position)">
                    <el-icon><Calendar /></el-icon>
                  </div>
                </el-tooltip>
                <div class="position-item-arrow">
                  <el-icon><ArrowRight /></el-icon>
                </div>
              </div>
            </div>
          </div>
        </el-card>

        <!-- 资源列表 -->
        <ResourceList
          :company-id="companyStore.currentCompany.id"
          @refresh="handleResourceRefresh"
          id="resources"
        />
        
        <!-- 面试列表 -->
        <el-card class="interview-card" v-if="companyStore.currentCompany.id" id="interviews">
          <template #header>
            <div class="card-header">
              <h3>面试记录</h3>
              <el-button 
                v-if="latestPosition" 
                type="primary" 
                size="small" 
                @click="router.push(`/resources/positions/${latestPosition.id}#interviews`)"
              >
                查看更多
              </el-button>
            </div>
          </template>
          
          <div class="card-subtitle" v-if="latestPosition">
            <span>显示最近面试时间的岗位记录，按面试时间排序先后显示</span>
          </div>
          
          <div v-if="loadingInterviews" class="loading-container">
            <el-skeleton :rows="3" animated />
          </div>

          <div v-else-if="!latestPosition" class="empty-state">
            <el-empty description="没有找到任何职位信息" />
          </div>
          
          <div v-else-if="recentInterviews.length === 0" class="empty-state">
            <el-empty description="暂无面试记录">
              <template #description>
                <p>{{ latestPosition.title || '当前职位' }}暂无面试记录</p>
              </template>
              <el-button 
                type="primary"
                @click="router.push(`/resources/positions/${latestPosition.id}#interviews`)"
              >
                添加面试
              </el-button>
            </el-empty>
          </div>
          
          <div v-else class="interviews-list">
            <div class="latest-position-info">
              <span class="latest-position-title">{{ latestPosition?.title }} 的最近面试</span>
            </div>
            
            <div 
              v-for="interview in recentInterviews.slice(0, 2)" 
              :key="interview.id" 
              class="interview-item"
              @click="router.push(`/resources/positions/${latestPosition?.id}#interviews`)"
            >
              <div class="interview-status">
                <el-tag 
                  :type="interview.status === 'completed' ? 'success' : 
                         interview.status === 'cancelled' ? 'danger' : 
                         interview.status === 'rescheduled' ? 'warning' : 'info'"
                  size="small"
                >
                  {{ getInterviewStatusLabel(interview.status || 'pending') }}
                </el-tag>
              </div>
              
              <div class="interview-content">
                <div class="interview-header">
                  <span class="interview-stage">{{ getInterviewStageLabel(interview.stage) }}</span>
                  <span class="interview-time">{{ formatInterviewTime(interview.dateTime) }}</span>
                </div>
                
                <div class="interview-details">
                  <div v-if="interview.location" class="detail-item">
                    <el-icon><Location /></el-icon>
                    <span>{{ interview.location }}</span>
                  </div>
                  
                  <div v-if="interview.interviewer" class="detail-item">
                    <el-icon><User /></el-icon>
                    <span>{{ interview.interviewer }}</span>
                  </div>
                </div>
                
                <div v-if="interview.notes" class="interview-notes">
                  <p>{{ interview.notes }}</p>
                </div>
              </div>
            </div>
            
            <div v-if="recentInterviews.length > 2" class="more-interviews">
              <el-button 
                type="text" 
                @click="router.push(`/resources/positions/${latestPosition?.id}#interviews`)"
              >
                查看全部面试记录 ({{ recentInterviews.length }})
              </el-button>
            </div>
          </div>
        </el-card>
      </div>
    </template>

    <!-- 编辑公司对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      title="编辑公司"
      width="600px"
      :close-on-click-modal="false"
      :destroy-on-close="true"
    >
      <CompanyForm
        ref="editFormRef"
        :initial-data="companyStore.currentCompany"
        :loading="companyStore.loading"
        @submit="handleUpdateCompany"
      />

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button 
            type="primary" 
            @click="submitEditForm"
            :loading="companyStore.loading"
          >
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>

    <el-dialog
      v-model="positionDialogVisible"
      title="添加职位"
      width="60%"
      destroy-on-close
    >
      <position-form
        v-if="positionDialogVisible"
        :position="newPosition"
        :loading="positionStore.loading"
        @submit="handlePositionSubmit"
        @cancel="positionDialogVisible = false"
      />
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Star, Top, Edit, Delete, Calendar, ArrowRight, Collection, Location, User } from '@element-plus/icons-vue';
import CompanyForm from '@/components/company/CompanyForm.vue';
import CompanyStatusTag from '@/components/company/CompanyStatusTag.vue';
import { useCompanyStore } from '@/store/companyStore';
import type { Company } from '@/types/company';
import { ElMessageBox, ElMessage } from 'element-plus';
import { usePositionStore } from '@/store/positionStore';
import type { Position } from '@/types/position';
import PositionItem from '@/components/position/PositionItem.vue';
import PositionForm from '@/components/position/PositionForm.vue';
import { WORK_TYPE_OPTIONS } from '@/types/position';
import ResourceList from '@/components/resource/ResourceList.vue';
import interviewService from '@/services/interviewService';
import type { Interview } from '@/types/interview';
import { INTERVIEW_STAGES, INTERVIEW_STATUS } from '@/types/interview';

const router = useRouter();
const route = useRoute();
const companyStore = useCompanyStore();
const positionStore = usePositionStore();

// 表单引用
const editFormRef = ref<any>();

// 对话框
const editDialogVisible = ref(false);
const positionDialogVisible = ref(false);
const newPosition = ref<Partial<Position>>({});
const latestPosition = ref<Position | null>(null);
const recentInterviews = ref<Interview[]>([]);
const loadingInterviews = ref(false);

// 页面初始化时加载数据
onMounted(async () => {
  const companyId = Number(route.params.id);
  if (!isNaN(companyId)) {
    await loadCompanyDetail(companyId);
    if (companyStore.currentCompany.id) {
      await loadPositions();
      
      // 获取最近更新的职位及其面试记录
      await loadRecentInterviews();
      
      // 处理锚点跳转
      if (route.hash === '#positions') {
        await nextTick();
        const element = document.getElementById('positions');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (route.hash === '#resources') {
        await nextTick();
        const element = document.getElementById('resources');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (route.hash === '#interviews') {
        await nextTick();
        const element = document.getElementById('interviews');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }
});

// 加载公司详情
const loadCompanyDetail = async (id: number) => {
  await companyStore.getCompany(id);
};

// 返回上一页
const goBack = () => {
  router.push('/resources');
};

// 打开编辑对话框
const openEditDialog = () => {
  editDialogVisible.value = true;
};

// 提交编辑表单
const submitEditForm = () => {
  if (editFormRef.value) {
    editFormRef.value.submitForm();
  }
};

// 处理更新公司
const handleUpdateCompany = async (company: Company) => {
  try {
    // 只发送后端需要的字段
    const updateData = {
      id: company.id,
      name: company.name,
      companyType: company.companyType,
      recruitmentUrl: company.recruitmentUrl,
      status: company.status,
      notes: company.notes,
      industry: company.industry,
      location: company.location,
      contactPerson: company.contactPerson,
      isStarred: company.isStarred,
      isPinned: company.isPinned
    };
    
    await companyStore.updateCompany(updateData);
    editDialogVisible.value = false;
    
    // 重新加载公司详情以获取最新数据
    if (company.id) {
      await loadCompanyDetail(company.id);
    }
  } catch (error) {
    console.error('更新公司失败', error);
  }
};

// 格式化URL
const formatUrl = (url: string) => {
  if (!url) return '';
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return 'https://' + url;
  }
  return url;
};

// 添加删除确认和处理功能
// 确认删除公司
const confirmDelete = () => {
  if (!companyStore.currentCompany?.id) return;
  
  ElMessageBox.confirm(
    `确定要删除公司 "${companyStore.currentCompany.name}" 吗？此操作不可恢复。`,
    '警告',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(() => {
    handleDeleteCompany();
  }).catch(() => {
    // 用户取消删除操作
  });
};

// 处理删除公司
const handleDeleteCompany = async () => {
  if (!companyStore.currentCompany?.id) return;
  
  try {
    const result = await companyStore.deleteCompany(companyStore.currentCompany.id);
    if (result) {
      // 删除成功后返回列表页
      router.push('/resources');
    }
  } catch (error) {
    console.error('删除公司失败', error);
    
    // 检查是否是外键约束错误
    if (error.response?.data?.message?.includes('foreign key constraint fails') ||
        error.message?.includes('foreign key constraint fails')) {
      ElMessageBox.alert(
        '该公司下存在关联的职位信息，无法直接删除。请先删除所有关联的职位后再尝试删除公司。',
        '删除失败',
        {
          confirmButtonText: '确定',
          type: 'error',
        }
      );
    } else {
      // 其他类型的错误
      ElMessageBox.alert(
        '删除公司时发生错误，请稍后重试。',
        '删除失败',
        {
          confirmButtonText: '确定',
          type: 'error',
        }
      );
    }
  }
};

const loadPositions = async () => {
  try {
    await positionStore.getPositionsByCompany(companyStore.currentCompany.id as number, 1);
  } catch (error) {
    console.error('加载职位列表失败', error);
  }
};

const showAddPositionDialog = () => {
  newPosition.value = {
    companyId: companyStore.currentCompany.id,
    applicationStatus: 'not_started',
    interviewStatus: 'not_scheduled',
    workType: 'full_time',
    priority: 2,
    salaryCurrency: 'CNY'
  };
  positionDialogVisible.value = true;
};

const handlePositionClick = (position: Position) => {
  router.push(`/resources/positions/${position.id}`);
};

const handlePositionSubmit = async (position: Partial<Position>) => {
  try {
    await positionStore.createPosition(position);
    ElMessage.success('职位添加成功');
    positionDialogVisible.value = false;
    await loadPositions();
  } catch (error) {
    ElMessage.error('添加职位失败');
    console.error(error);
  }
};

const getWorkTypeLabel = (type: string) => {
  const option = WORK_TYPE_OPTIONS.find(opt => opt.value === type);
  return option ? option.label : type;
};

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  });
};

const navigateToInterviews = (position: Position) => {
  // 阻止事件冒泡，避免触发整个行的点击事件
  event?.stopPropagation();
  if (position.id) {
    router.push(`/resources/positions/${position.id}#interviews`);
  }
};

const handleResourceRefresh = () => {
  // 实现资源列表刷新逻辑
  console.log('资源列表刷新');
};

// 查看职位相关资源
const handleViewResources = (position: Position) => {
  router.push(`/resources/positions/${position.id}`);
};

const handleEditPosition = (position: Position) => {
  // 实现编辑职位逻辑
  console.log('编辑职位', position);
};

const handleDeletePosition = (position: Position) => {
  // 实现删除职位逻辑
  console.log('删除职位', position);
};

// 加载最近更新的职位及其面试记录
const loadRecentInterviews = async () => {
  if (!positionStore.positions.length) return;
  
  loadingInterviews.value = true;
  console.log('开始获取面试记录，职位数量:', positionStore.positions.length);
  
  try {
    // 存储所有职位的最新面试记录
    const positionInterviews: { position: Position; interviews: Interview[] }[] = [];
    
    // 遍历所有职位，获取每个职位的面试记录
    for (const position of positionStore.positions) {
      if (position.id) {
        try {
          console.log(`获取职位 ${position.id} (${position.title || 'unnamed'}) 的面试记录`);
          const response = await interviewService.getInterviewsByPosition(position.id);
          console.log(`职位 ${position.id} 的响应:`, response);
          
          // 检查响应的具体结构
          let interviews: Interview[] = [];
          if (response && response.code === 200 && response.data) {
            console.log('响应数据类型:', typeof response.data);
            if (Array.isArray(response.data)) {
              interviews = response.data;
              console.log('数据是数组');
            } else if (response.data.content && Array.isArray(response.data.content)) {
              interviews = response.data.content;
              console.log('数据是分页对象');
            } else {
              console.warn('未知的响应数据结构:', response.data);
            }
          } else if (Array.isArray(response)) {
            interviews = response;
            console.log('直接返回的是数组');
          } else if (response && response.content && Array.isArray(response.content)) {
            interviews = response.content;
            console.log('直接返回的是分页对象');
          }
          
          console.log(`找到 ${interviews.length} 条面试记录`);
          
          if (interviews.length > 0) {
            // 按面试时间排序，最近的排在前面
            const sortedInterviews = [...interviews].sort((a, b) => {
              // 计算与当前时间的差值，用绝对值来处理过去和将来的面试
              const now = new Date().getTime();
              const diffA = Math.abs(new Date(a.dateTime).getTime() - now);
              const diffB = Math.abs(new Date(b.dateTime).getTime() - now);
              return diffA - diffB;
            });
            
            // 存储该职位及其面试记录
            positionInterviews.push({
              position,
              interviews: sortedInterviews
            });
          }
        } catch (error) {
          console.error(`获取职位 ${position.id} 的面试记录失败`, error);
        }
      }
    }
    
    console.log('收集到的职位面试记录:', positionInterviews.length);
    
    // 如果有面试记录，找出最近面试时间的职位
    if (positionInterviews.length > 0) {
      // 按最近面试时间排序所有职位
      positionInterviews.sort((a, b) => {
        // 计算每个职位最近的面试时间与当前时间的差值
        const now = new Date().getTime();
        const latestA = a.interviews[0]?.dateTime || '1970-01-01';
        const latestB = b.interviews[0]?.dateTime || '1970-01-01';
        const diffA = Math.abs(new Date(latestA).getTime() - now);
        const diffB = Math.abs(new Date(latestB).getTime() - now);
        return diffA - diffB;
      });
      
      // 取最新面试记录的职位
      const latest = positionInterviews[0];
      console.log('最新面试记录的职位:', latest.position.title, '面试数量:', latest.interviews.length);
      latestPosition.value = latest.position;
      recentInterviews.value = latest.interviews;
    } else {
      // 没有面试记录，则显示最新更新的职位
      console.log('没有找到面试记录，显示最新更新的职位');
      const sortedPositions = [...positionStore.positions].sort((a, b) => {
        return new Date(b.updateTime || 0).getTime() - new Date(a.updateTime || 0).getTime();
      });
      
      if (sortedPositions.length > 0) {
        latestPosition.value = sortedPositions[0];
        recentInterviews.value = [];
        console.log('选择了最新更新的职位:', latestPosition.value.title);
      }
    }
  } catch (error) {
    console.error('加载面试记录失败', error);
  } finally {
    loadingInterviews.value = false;
  }
};

// 获取面试阶段标签
const getInterviewStageLabel = (stage: string) => {
  return INTERVIEW_STAGES[stage as keyof typeof INTERVIEW_STAGES] || stage;
};

// 获取面试状态标签
const getInterviewStatusLabel = (status: string) => {
  return INTERVIEW_STATUS[status as keyof typeof INTERVIEW_STATUS] || status;
};

// 格式化面试时间
const formatInterviewTime = (dateTime: string) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};
</script>

<style scoped>
.company-detail-view {
  padding: 24px;
}

.loading-container, .empty-container {
  margin-top: 24px;
}

.company-detail-container {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-card {
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.card-title {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-title h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
}

.company-flags {
  display: flex;
  gap: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px 40px;
  margin-top: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.full-width {
  grid-column: 1 / -1;
}

.label {
  color: #909399;
  font-size: 14px;
}

.value {
  font-size: 16px;
}

.note-content {
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  line-height: 1.6;
}

.status-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.tabs-card {
  border-radius: 8px;
}

.empty-tab {
  padding: 40px 0;
  display: flex;
  justify-content: center;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}

.card-actions {
  display: flex;
  gap: 12px;
}

.position-card {
  margin-top: 24px;
}

.positions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.position-list-item {
  background: white;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #dcdfe6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.position-list-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #c6e2ff;
}

.position-item-content {
  flex: 1;
}

.position-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.position-title {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #303133;
}

.position-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.position-meta {
  margin-top: 8px;
  display: flex;
  gap: 24px;
  color: #606266;
  font-size: 13px;
}

.salary-range {
  color: #67c23a;
  font-weight: 500;
}

.deadline {
  display: flex;
  align-items: center;
  gap: 4px;
}

.position-item-arrow {
  color: #c0c4cc;
  font-size: 16px;
  transition: transform 0.3s ease;
}

.position-list-item:hover .position-item-arrow {
  transform: translateX(4px);
  color: #409eff;
}

.empty-state {
  padding: 24px;
  display: flex;
  justify-content: center;
}

.position-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.action-icon {
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-icon.interview {
  color: #e6a23c;
  background-color: #fdf6ec;
}

.action-icon.interview:hover {
  background-color: #faecd8;
  transform: scale(1.1);
}

.action-icon.view {
  color: #67c23a;
  background-color: #f0f9eb;
}

.action-icon.view:hover {
  background-color: #e1f3d8;
  transform: scale(1.1);
}

.action-icon.edit {
  color: #409eff;
  background-color: #f0f9eb;
}

.action-icon.edit:hover {
  background-color: #e1f3d8;
  transform: scale(1.1);
}

.action-icon.delete {
  color: #f56c6c;
  background-color: #fef0f0;
}

.action-icon.delete:hover {
  background-color: #fde2e2;
  transform: scale(1.1);
}

.interview-card {
  margin-top: 24px;
}

.interviews-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.interview-item {
  background: white;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #dcdfe6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.interview-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #c6e2ff;
}

.interview-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.interview-content {
  flex: 1;
}

.interview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.interview-stage {
  font-weight: 500;
}

.interview-time {
  color: #909399;
  font-size: 13px;
}

.interview-details {
  margin-top: 8px;
  display: flex;
  gap: 24px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.interview-notes {
  margin-top: 8px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  line-height: 1.6;
}

.latest-position-info {
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.latest-position-title {
  font-weight: 500;
}

.more-interviews {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.card-subtitle {
  margin: -12px 0 12px 0;
  padding: 0 12px;
  color: #909399;
  font-size: 12px;
  font-style: italic;
}

@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style> 